import {
  WSActions,
  WSElement,
  WSGrid,
  WSInfoBox,
  WSList,
  WSPanel,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  InsuranceCoverageSource,
  InsuranceMonitor,
  PayeeRequirement,
  RequirementType
} from "@wingspanhq/wingspan-api-sdk";
import React, { useMemo } from "react";

import { selectorSearchRowName } from "../../../../shared/selectors/selectorSearchRowName";
import { queriesPayerRows } from "../../../../v3/query/queriesPayerRows";
import { InsuranceInstructionsView } from "../../../RequirementDefinitions/routes/RouteCreateRequirementByTemplate/components/InsuranceInstructionsView";
import { getRequirementDefinitionTemplateByParams } from "../../../RequirementDefinitions/utils/getRequirementDefinitionTemplateByParams";
import { selectorIsAcceptedSourceTypeIncluded } from "../../modules/InsuranceCoverage/selectors/selectorIsAcceptedSourceTypeIncluded";
import { selectorPolicyTypesFromMonitor } from "../../modules/InsuranceCoverage/selectors/selectorPolicyTypesFromMonitor";
import { ContextItem, RequirementContextType } from "../../types";
import { RequirementsConfigResult } from "../../useRequirementsConfig";

export type RequirementDetailsInstructionsProps = RequirementsConfigResult & {
  contextType: RequirementContextType;
  onSuccessAction: () => void;
  onCloseAction: () => void;
  customContext?: ContextItem;
  requirement: PayeeRequirement;
  instructions: string;
  monitor?: InsuranceMonitor;
};

export const RequirementDetailsInstructions: React.FC<
  RequirementDetailsInstructionsProps
> = ({
  requirement,
  contextType,
  isLoading,
  getRequirementDetailsWidgets,
  getRequirementInstructionsWidgets,
  getRequirementInstructions,
  instructions,
  onSuccessAction,
  onCloseAction,
  monitor
}) => {
  // The client name comes off the payer search row keyed by `payerAccountId`,
  // the same lookup as `useRequirementsConfig.getRequirementPayerName`. It
  // deliberately does not read a `Payer` by id — a v3 `Payer.id` is not the
  // payer's account id, so `getPayer({ payerId: payerAccountId })` 404s.
  const queryPayerRow = queriesPayerRows.searchPayerRows.useQuery(
    {
      filter: { payerAccountId: { eq: requirement.payerAccountId! } },
      page: { size: 1 }
    },
    {
      enabled:
        requirement.type === RequirementType.BIOMETRIC_IDENTITY_VERIFICATION &&
        !!requirement.payerAccountId,
      retry: false
    }
  );

  const requirementInstructionsWidgets =
    getRequirementInstructionsWidgets(requirement);
  const viewInstructions =
    getRequirementInstructions(requirement)?.(instructions);

  const payerName = useMemo(() => {
    const payerRow = queryPayerRow.data?.data?.[0];

    return payerRow ? selectorSearchRowName(payerRow) : "Payer";
  }, [queryPayerRow.data]);

  // v1 gated the "accepts Wingspan's master policy" copy on the definition's
  // `settings.coverages[].acceptedSourceTypes`. On v3 that same list rides the
  // monitor's expanded configuration, which the opener already asks for via
  // INSURANCE_MONITOR_EXPAND — the read `RequirementInformation` makes too.
  const acceptWingspanMasterPolicy = !!selectorIsAcceptedSourceTypeIncluded({
    acceptedSourceType: InsuranceCoverageSource.PLAN_ENROLLMENT,
    insuranceMonitorConfiguration: monitor?._configuration
  });

  // TODO(v3-verify): `selectorPolicyTypesFromMonitor` (already migrated,
  // owned by the InsuranceCoverage module) returns a single display label,
  // where `getRequirementDefinitionTemplateByParams`/`InsuranceInstructionsView`
  // (both owned by RequirementDefinitions, out of this wave's scope) still
  // expect the v1 `IPolicyType[]` / `PolicyType[]` array shape. Kept
  // consistent with the same pattern already used in the migrated
  // `useRequirementsConfig.tsx` rather than inventing a local adapter; needs
  // resolving when those modules migrate.
  const template = getRequirementDefinitionTemplateByParams({
    policyType: monitor ? selectorPolicyTypesFromMonitor(monitor) : undefined,
    type: RequirementType.INSURANCE_COVERAGE
  });

  return (
    <WSElement shimmer={isLoading}>
      <WSList gap="XL">
        <WSGrid>
          {requirementInstructionsWidgets ? null : (
            <WSGrid.Item span={{ s: "2" }} />
          )}
          <WSGrid.Item span={{ s: "8" }}>
            {requirement.type !== RequirementType.INSURANCE_COVERAGE ? (
              <>
                <WSText.Paragraph weight="medium">
                  Requirement instructions
                </WSText.Paragraph>
                <WSText.ParagraphSm color="gray500" mt="S">
                  Review the details to ensure you have the necessary
                  information to complete the requirement.
                </WSText.ParagraphSm>

                <WSPanel mt="M" p="L">
                  {viewInstructions}

                  {requirement.type ===
                  RequirementType.BIOMETRIC_IDENTITY_VERIFICATION ? (
                    <WSInfoBox mt="M" shimmer={queryPayerRow.isLoading}>
                      {payerName} takes the threat of employment-related
                      identity theft seriously and requires all contractors to
                      undergo this verification process. {payerName} does not
                      collect, process, store, or access any biometric data
                      collected during the verification process, as defined by
                      applicable data privacy laws.
                    </WSInfoBox>
                  ) : null}
                </WSPanel>
              </>
            ) : (
              <InsuranceInstructionsView
                allowMasterPolicy={acceptWingspanMasterPolicy}
                insuranceName={requirement.name || template.name}
                policyTypes={
                  monitor ? selectorPolicyTypesFromMonitor(monitor) : undefined
                }
              />
            )}

            <WSActions
              alignment="fill"
              buttons={[
                {
                  kind: "Primary",
                  label: "Continue",
                  onClick: () => {
                    onSuccessAction();
                  }
                },
                {
                  kind: "Secondary",
                  label: "Cancel",
                  onClick: () => {
                    onCloseAction();
                  }
                }
              ]}
              mt="XL"
            ></WSActions>
          </WSGrid.Item>
          <WSGrid.Item span={{ s: "4" }}>
            {requirementInstructionsWidgets}
          </WSGrid.Item>
        </WSGrid>
      </WSList>
    </WSElement>
  );
};
